import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import {
  BarChart3,
  TrendingUp,
  Users,
  Target,
  Activity,
  Download,
} from 'lucide-react'
import { crmReportsApi, pipelineApi, crmApi } from '@/lib/api'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { formatCurrency } from '@/lib/utils'

export default function CRMReportsPage() {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState('leads')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [selectedPipelineId, setSelectedPipelineId] = useState<string | undefined>()
  const [selectedSourceId, setSelectedSourceId] = useState<string | undefined>()

  // Fetch filter options
  const { data: pipelines } = useQuery({
    queryKey: ['pipelines'],
    queryFn: () => pipelineApi.getPipelines(),
  })

  const { data: sources } = useQuery({
    queryKey: ['lead-sources'],
    queryFn: () => crmApi.getLeadSources(true),
  })

  const filters = {
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    pipeline_id: selectedPipelineId,
    source_id: selectedSourceId,
  }

  // Report queries
  const { data: leadReport, isLoading: loadingLeads } = useQuery({
    queryKey: ['crm-reports', 'leads', filters],
    queryFn: () => crmReportsApi.getLeadReport(filters),
    enabled: activeTab === 'leads',
  })

  const { data: pipelineReport, isLoading: loadingPipeline } = useQuery({
    queryKey: ['crm-reports', 'pipeline', filters],
    queryFn: () => crmReportsApi.getPipelineReport(filters),
    enabled: activeTab === 'pipeline',
  })

  const { data: activityReport, isLoading: loadingActivity } = useQuery({
    queryKey: ['crm-reports', 'activities', filters],
    queryFn: () => crmReportsApi.getActivityReport(filters),
    enabled: activeTab === 'activities',
  })

  const { data: conversionReport, isLoading: loadingConversion } = useQuery({
    queryKey: ['crm-reports', 'conversions', filters],
    queryFn: () => crmReportsApi.getConversionReport(filters),
    enabled: activeTab === 'conversions',
  })

  const { data: salesReport, isLoading: loadingSales } = useQuery({
    queryKey: ['crm-reports', 'sales', filters],
    queryFn: () => crmReportsApi.getSalesReport(filters),
    enabled: activeTab === 'sales',
  })

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('crm.reports.title', 'CRM Reports')}</h1>
          <p className="text-muted-foreground">
            {t('crm.reports.subtitle', 'Analyze your sales and lead performance')}
          </p>
        </div>
        <Button variant="outline">
          <Download className="mr-2 h-4 w-4" />
          {t('common.export', 'Export')}
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid gap-4 md:grid-cols-4">
            <div className="space-y-2">
              <Label>{t('common.dateFrom', 'Date From')}</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>{t('common.dateTo', 'Date To')}</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>{t('crm.reports.pipeline', 'Pipeline')}</Label>
              <Select value={selectedPipelineId || 'all'} onValueChange={(v) => setSelectedPipelineId(v === 'all' ? undefined : v)}>
                <SelectTrigger>
                  <SelectValue placeholder={t('common.all', 'All')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('common.all', 'All')}</SelectItem>
                  {pipelines?.map((p) => (
                    <SelectItem key={p.pipeline_id} value={p.pipeline_id}>{p.pipeline_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('crm.reports.source', 'Lead Source')}</Label>
              <Select value={selectedSourceId || 'all'} onValueChange={(v) => setSelectedSourceId(v === 'all' ? undefined : v)}>
                <SelectTrigger>
                  <SelectValue placeholder={t('common.all', 'All')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('common.all', 'All')}</SelectItem>
                  {sources?.map((s) => (
                    <SelectItem key={s.source_id} value={s.source_id}>{s.source_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Report Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="leads" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            {t('crm.reports.leads', 'Leads')}
          </TabsTrigger>
          <TabsTrigger value="pipeline" className="flex items-center gap-2">
            <Target className="h-4 w-4" />
            {t('crm.reports.pipeline', 'Pipeline')}
          </TabsTrigger>
          <TabsTrigger value="activities" className="flex items-center gap-2">
            <Activity className="h-4 w-4" />
            {t('crm.reports.activities', 'Activities')}
          </TabsTrigger>
          <TabsTrigger value="conversions" className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            {t('crm.reports.conversions', 'Conversions')}
          </TabsTrigger>
          <TabsTrigger value="sales" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            {t('crm.reports.sales', 'Sales')}
          </TabsTrigger>
        </TabsList>

        {/* Leads Report */}
        <TabsContent value="leads" className="space-y-4">
          {loadingLeads ? (
            <Card><CardContent className="py-8 text-center text-muted-foreground">{t('common.loading', 'Loading...')}</CardContent></Card>
          ) : (
            <>
              <div className="grid gap-4 md:grid-cols-4">
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">{t('crm.reports.totalNew', 'Total New')}</CardTitle></CardHeader>
                  <CardContent><div className="text-2xl font-bold">{leadReport?.summary?.total_new || 0}</div></CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">{t('crm.reports.totalConverted', 'Total Converted')}</CardTitle></CardHeader>
                  <CardContent><div className="text-2xl font-bold text-green-600">{leadReport?.summary?.total_converted || 0}</div></CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">{t('crm.reports.totalLost', 'Total Lost')}</CardTitle></CardHeader>
                  <CardContent><div className="text-2xl font-bold text-red-600">{leadReport?.summary?.total_lost || 0}</div></CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">{t('crm.reports.conversionRate', 'Conversion Rate')}</CardTitle></CardHeader>
                  <CardContent><div className="text-2xl font-bold">{(leadReport?.summary?.conversion_rate || 0).toFixed(1)}%</div></CardContent>
                </Card>
              </div>
              <Card>
                <CardHeader><CardTitle>{t('crm.reports.leadTrend', 'Lead Trend')}</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {leadReport?.data?.map((item) => (
                      <div key={item.date} className="flex items-center justify-between py-2 border-b last:border-0">
                        <span className="font-medium">{item.date}</span>
                        <div className="flex gap-6">
                          <span className="text-sm">New: {item.new_leads}</span>
                          <span className="text-sm text-green-600">Converted: {item.converted_leads}</span>
                          <span className="text-sm text-red-600">Lost: {item.lost_leads}</span>
                        </div>
                      </div>
                    ))}
                    {(!leadReport?.data || leadReport.data.length === 0) && (
                      <div className="text-center py-4 text-muted-foreground">{t('common.noData', 'No data')}</div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Pipeline Report */}
        <TabsContent value="pipeline" className="space-y-4">
          {loadingPipeline ? (
            <Card><CardContent className="py-8 text-center text-muted-foreground">{t('common.loading', 'Loading...')}</CardContent></Card>
          ) : (
            <>
              <div className="grid gap-4 md:grid-cols-4">
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">{t('crm.reports.totalDeals', 'Total Deals')}</CardTitle></CardHeader>
                  <CardContent><div className="text-2xl font-bold">{pipelineReport?.summary?.total_deals || 0}</div></CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">{t('crm.reports.totalValue', 'Total Value')}</CardTitle></CardHeader>
                  <CardContent><div className="text-2xl font-bold">{formatCurrency(pipelineReport?.summary?.total_value || 0)}</div></CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">{t('crm.reports.avgCycleTime', 'Avg Cycle Time')}</CardTitle></CardHeader>
                  <CardContent><div className="text-2xl font-bold">{pipelineReport?.summary?.avg_cycle_time || 0} days</div></CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">{t('crm.reports.winRate', 'Win Rate')}</CardTitle></CardHeader>
                  <CardContent><div className="text-2xl font-bold">{(pipelineReport?.summary?.win_rate || 0).toFixed(1)}%</div></CardContent>
                </Card>
              </div>
              <Card>
                <CardHeader><CardTitle>{t('crm.reports.stageBreakdown', 'Stage Breakdown')}</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {pipelineReport?.stages?.map((stage) => (
                      <div key={stage.stage_id} className="flex items-center justify-between py-2 border-b last:border-0">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: stage.stage_color }} />
                          <span className="font-medium">{stage.stage_name}</span>
                        </div>
                        <div className="flex gap-6 text-sm">
                          <span>Current: {stage.current_count}</span>
                          <span>Entered: {stage.entered_count}</span>
                          <span>Avg Days: {stage.avg_days_in_stage}</span>
                          <span>{formatCurrency(stage.total_value)}</span>
                        </div>
                      </div>
                    ))}
                    {(!pipelineReport?.stages || pipelineReport.stages.length === 0) && (
                      <div className="text-center py-4 text-muted-foreground">{t('common.noData', 'No data')}</div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Activities Report */}
        <TabsContent value="activities" className="space-y-4">
          {loadingActivity ? (
            <Card><CardContent className="py-8 text-center text-muted-foreground">{t('common.loading', 'Loading...')}</CardContent></Card>
          ) : (
            <>
              <div className="grid gap-4 md:grid-cols-3">
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">{t('crm.reports.totalActivities', 'Total Activities')}</CardTitle></CardHeader>
                  <CardContent><div className="text-2xl font-bold">{activityReport?.summary?.total_activities || 0}</div></CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">{t('crm.reports.avgPerDay', 'Avg Per Day')}</CardTitle></CardHeader>
                  <CardContent><div className="text-2xl font-bold">{(activityReport?.summary?.avg_per_day || 0).toFixed(1)}</div></CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">{t('crm.reports.byType', 'By Type')}</CardTitle></CardHeader>
                  <CardContent>
                    <div className="flex gap-2 flex-wrap">
                      {activityReport?.summary?.by_type && Object.entries(activityReport.summary.by_type).map(([type, count]) => (
                        <span key={type} className="text-xs bg-muted px-2 py-1 rounded">{type}: {count}</span>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
              <Card>
                <CardHeader><CardTitle>{t('crm.reports.activityTrend', 'Activity Trend')}</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {activityReport?.data?.map((item) => (
                      <div key={item.date} className="flex items-center justify-between py-2 border-b last:border-0">
                        <span className="font-medium">{item.date}</span>
                        <div className="flex gap-4 text-sm">
                          <span>Calls: {item.calls}</span>
                          <span>Emails: {item.emails}</span>
                          <span>Meetings: {item.meetings}</span>
                          <span>Tasks: {item.tasks}</span>
                          <span className="font-medium">Total: {item.total}</span>
                        </div>
                      </div>
                    ))}
                    {(!activityReport?.data || activityReport.data.length === 0) && (
                      <div className="text-center py-4 text-muted-foreground">{t('common.noData', 'No data')}</div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Conversions Report */}
        <TabsContent value="conversions" className="space-y-4">
          {loadingConversion ? (
            <Card><CardContent className="py-8 text-center text-muted-foreground">{t('common.loading', 'Loading...')}</CardContent></Card>
          ) : (
            <>
              <div className="grid gap-4 md:grid-cols-4">
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">{t('crm.reports.totalLeads', 'Total Leads')}</CardTitle></CardHeader>
                  <CardContent><div className="text-2xl font-bold">{conversionReport?.summary?.total_leads || 0}</div></CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">{t('crm.reports.totalConverted', 'Total Converted')}</CardTitle></CardHeader>
                  <CardContent><div className="text-2xl font-bold text-green-600">{conversionReport?.summary?.total_converted || 0}</div></CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">{t('crm.reports.overallRate', 'Overall Rate')}</CardTitle></CardHeader>
                  <CardContent><div className="text-2xl font-bold">{(conversionReport?.summary?.overall_conversion_rate || 0).toFixed(1)}%</div></CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">{t('crm.reports.avgTimeToConvert', 'Avg Time to Convert')}</CardTitle></CardHeader>
                  <CardContent><div className="text-2xl font-bold">{conversionReport?.summary?.avg_time_to_convert || 0} days</div></CardContent>
                </Card>
              </div>
              <Card>
                <CardHeader><CardTitle>{t('crm.reports.conversionBySource', 'Conversion by Source')}</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {conversionReport?.data?.map((item) => (
                      <div key={item.source_id} className="flex items-center justify-between py-2 border-b last:border-0">
                        <span className="font-medium">{item.source_name}</span>
                        <div className="flex gap-6 text-sm">
                          <span>Leads: {item.leads}</span>
                          <span className="text-green-600">Converted: {item.converted}</span>
                          <span className="font-medium">{item.conversion_rate.toFixed(1)}%</span>
                          <span>{formatCurrency(item.total_deal_value)}</span>
                        </div>
                      </div>
                    ))}
                    {(!conversionReport?.data || conversionReport.data.length === 0) && (
                      <div className="text-center py-4 text-muted-foreground">{t('common.noData', 'No data')}</div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Sales Report */}
        <TabsContent value="sales" className="space-y-4">
          {loadingSales ? (
            <Card><CardContent className="py-8 text-center text-muted-foreground">{t('common.loading', 'Loading...')}</CardContent></Card>
          ) : (
            <>
              <div className="grid gap-4 md:grid-cols-5">
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">{t('crm.reports.dealsWon', 'Deals Won')}</CardTitle></CardHeader>
                  <CardContent><div className="text-2xl font-bold text-green-600">{salesReport?.summary?.total_won || 0}</div></CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">{t('crm.reports.dealsLost', 'Deals Lost')}</CardTitle></CardHeader>
                  <CardContent><div className="text-2xl font-bold text-red-600">{salesReport?.summary?.total_lost || 0}</div></CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">{t('crm.reports.totalRevenue', 'Total Revenue')}</CardTitle></CardHeader>
                  <CardContent><div className="text-2xl font-bold">{formatCurrency(salesReport?.summary?.total_revenue || 0)}</div></CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">{t('crm.reports.avgDealSize', 'Avg Deal Size')}</CardTitle></CardHeader>
                  <CardContent><div className="text-2xl font-bold">{formatCurrency(salesReport?.summary?.avg_deal_size || 0)}</div></CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">{t('crm.reports.winRate', 'Win Rate')}</CardTitle></CardHeader>
                  <CardContent><div className="text-2xl font-bold">{(salesReport?.summary?.overall_win_rate || 0).toFixed(1)}%</div></CardContent>
                </Card>
              </div>
              <Card>
                <CardHeader><CardTitle>{t('crm.reports.salesTrend', 'Sales Trend')}</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {salesReport?.data?.map((item) => (
                      <div key={item.period} className="flex items-center justify-between py-2 border-b last:border-0">
                        <span className="font-medium">{item.period}</span>
                        <div className="flex gap-6 text-sm">
                          <span className="text-green-600">Won: {item.deals_won}</span>
                          <span className="text-red-600">Lost: {item.deals_lost}</span>
                          <span>Revenue: {formatCurrency(item.revenue)}</span>
                          <span>Avg: {formatCurrency(item.avg_deal_size)}</span>
                          <span className="font-medium">{item.win_rate.toFixed(1)}%</span>
                        </div>
                      </div>
                    ))}
                    {(!salesReport?.data || salesReport.data.length === 0) && (
                      <div className="text-center py-4 text-muted-foreground">{t('common.noData', 'No data')}</div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}

